/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import style from './DiviNavigation.scss?inline';
import { DiviNavigationElement } from './DiviNavigationElement';
import { useCSS } from '../../provider/CSSProvider';
import { DiviNavigationIds } from '../../models/diviProgress';
import { getPageRoute, PageType } from '../../models/diviPage';
import { useContext, useMemo } from 'react';
import { ReportsAPIContext } from '../../provider/ReportsAPIProvider';
import {
  isEqualMissionDataOperational,
  isEqualMissionDataPatientMaster,
  isEqualPatient,
  isEqualPatientSymptomOnset,
} from './DiviNavigation.functions';
import { ApplicationConfig } from '../../config/application.config';

function DiviNavigation() {
  useCSS(style);

  const { patient, lastSynchronizedBaseData, missionData, lastSynchronizedMissionData } = useContext(ReportsAPIContext);

  const synchronizationEnabled = ApplicationConfig.immediatelySyncToConference;

  const hasPatientChanged = useMemo(
    () => !isEqualPatient(patient, lastSynchronizedBaseData) && synchronizationEnabled,
    [patient, lastSynchronizedBaseData, synchronizationEnabled]
  );

  const hasPatientSymptomChanged = useMemo(
    () => !isEqualPatientSymptomOnset(patient, lastSynchronizedBaseData) && synchronizationEnabled,
    [patient, lastSynchronizedBaseData, synchronizationEnabled]
  );

  const hasMissionDataOperationalChanged = useMemo(
    // CWCONF-31200: this indicates initially always a change because lastSynchronizedMissionData is not (fully)
    // initialized till OperationalData was opened once
    () => !isEqualMissionDataOperational(missionData, lastSynchronizedMissionData) && synchronizationEnabled,
    [missionData, lastSynchronizedMissionData, synchronizationEnabled]
  );

  const hasMissionDataPatientMasterChanged = useMemo(
    () => !isEqualMissionDataPatientMaster(missionData, lastSynchronizedMissionData) && synchronizationEnabled,
    [missionData, lastSynchronizedMissionData, synchronizationEnabled]
  );

  return (
    <div id='divi-navigation'>
      <div id='divi-protocoll-navigation'>
        <div className='divi-container divi-one'>
          <DiviNavigationElement
            id={DiviNavigationIds.Pat}
            route={getPageRoute(PageType.PatientMasterData)}
            showNewDataStatusIcon={hasPatientChanged || hasMissionDataPatientMasterChanged}
          />
          <DiviNavigationElement
            id={DiviNavigationIds.IncidentData}
            route={getPageRoute(PageType.OperationalData)}
            showNewDataStatusIcon={hasMissionDataOperationalChanged || hasPatientSymptomChanged}
          />
          <DiviNavigationElement id={DiviNavigationIds.Anamnese} route={getPageRoute(PageType.Anamnese)} />
          <DiviNavigationElement id={DiviNavigationIds.Neurology} route={getPageRoute(PageType.Neurology)} />
          <DiviNavigationElement id={DiviNavigationIds.DataInitial} route={getPageRoute(PageType.DataInitial)} />
          <DiviNavigationElement
            id={DiviNavigationIds.MedicalConditions}
            route={getPageRoute(PageType.MedicalConditions)}
          />
          <DiviNavigationElement id={DiviNavigationIds.Injuries} route={getPageRoute(PageType.Injuries)} />
          <DiviNavigationElement
            id={DiviNavigationIds.InitialDiagnosis}
            route={getPageRoute(PageType.DiagnosisInitial)}
          />
        </div>
        <div className='divi-container divi-two'>
          <DiviNavigationElement id={DiviNavigationIds.Course} route={getPageRoute(PageType.Course)} />
          <DiviNavigationElement id={DiviNavigationIds.Medication} route={getPageRoute(PageType.Medication)} />
          <DiviNavigationElement
            id={DiviNavigationIds.ReanimationDeath}
            route={getPageRoute(PageType.ReanimationDeath)}
          />
          <DiviNavigationElement id={DiviNavigationIds.Measures} route={getPageRoute(PageType.Measures)} />
          <DiviNavigationElement id={DiviNavigationIds.Handover} route={getPageRoute(PageType.Handover)} />
          <DiviNavigationElement id={DiviNavigationIds.Specifics} route={getPageRoute(PageType.Specifics)} />
          <DiviNavigationElement id={DiviNavigationIds.HandoverTo} route={getPageRoute(PageType.HandoverTo)} />
          <DiviNavigationElement id={DiviNavigationIds.Remarks} route={getPageRoute(PageType.Remarks)} />
        </div>
      </div>
    </div>
  );
}

export default DiviNavigation;
